import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

interface CultureActivity {
  id: number;
  title: string;
  description: string;
  image: string;
  price: number;
  duration: string;
  maxParticipants: number;
  languages: string[];
  availableTimes: string[];
  location: string;
  category: string;
  instructor: string;
  materials: string[];
}

const CulturePage: React.FC = () => {
  const { translations } = useLanguage();
  const [selectedActivity, setSelectedActivity] = useState<CultureActivity | null>(null);
  const [showModal, setShowModal] = useState(false);

  const cultureActivities: CultureActivity[] = [
    {
      id: 1,
      title: "阿美族傳統料理體驗",
      description: "學習製作阿美族傳統美食，包括竹筒飯、野菜料理等。由部落長者親自指導，深入了解原住民飲食文化。",
      image: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop",
      price: 800,
      duration: "3小時",
      maxParticipants: 12,
      languages: ["中文", "阿美族語"],
      availableTimes: ["09:00", "14:00"],
      location: "阿美族文化村",
      category: "料理體驗",
      instructor: "林美花 老師",
      materials: ["竹筒", "野菜", "調味料", "廚具"]
    },
    {
      id: 2,
      title: "手工編織技藝",
      description: "學習傳統手工編織技術，製作實用的生活用品。體驗原住民精湛的手工藝，帶回獨特的手作紀念品。",
      image: "https://images.unsplash.com/photo-1513519245088-0e12902e35ca?w=400&h=300&fit=crop",
      price: 600,
      duration: "2.5小時",
      maxParticipants: 8,
      languages: ["中文", "英文"],
      availableTimes: ["10:00", "15:00"],
      location: "花蓮文創園區",
      category: "手工藝",
      instructor: "陳雅文 老師",
      materials: ["編織材料", "工具", "教學手冊"]
    },
    {
      id: 3,
      title: "太魯閣族音樂體驗",
      description: "學習太魯閣族傳統樂器演奏，包括口簧琴、木琴等。體驗原住民音樂的魅力，感受山林間的天籟之音。",
      image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop",
      price: 500,
      duration: "2小時",
      maxParticipants: 15,
      languages: ["中文", "太魯閣族語"],
      availableTimes: ["14:00", "16:00"],
      location: "太魯閣文化館",
      category: "音樂體驗",
      instructor: "王大山 老師",
      materials: ["傳統樂器", "樂譜", "錄音設備"]
    },
    {
      id: 4,
      title: "植物染布體驗",
      description: "使用花蓮在地植物進行天然染布，學習傳統染布技術。了解植物特性，創造獨一無二的染布作品。",
      image: "https://images.unsplash.com/photo-1513519245088-0e12902e35ca?w=400&h=300&fit=crop",
      price: 700,
      duration: "3小時",
      maxParticipants: 10,
      languages: ["中文", "英文", "日文"],
      availableTimes: ["09:00", "13:00"],
      location: "花蓮植物園",
      category: "染布體驗",
      instructor: "李雅芳 老師",
      materials: ["白布", "植物染料", "染布工具", "防護用品"]
    },
    {
      id: 5,
      title: "原住民舞蹈教學",
      description: "學習阿美族傳統舞蹈，體驗原住民文化的活力與熱情。在專業舞者指導下，感受舞蹈中的文化內涵。",
      image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop",
      price: 400,
      duration: "1.5小時",
      maxParticipants: 20,
      languages: ["中文", "阿美族語"],
      availableTimes: ["16:00", "19:00"],
      location: "花蓮文化中心",
      category: "舞蹈體驗",
      instructor: "張美玲 老師",
      materials: ["傳統服飾", "音樂設備", "舞蹈場地"]
    },
    {
      id: 6,
      title: "石頭彩繪藝術",
      description: "在花蓮特有的石頭上進行彩繪創作，學習原住民圖騰藝術。發揮創意，創造屬於自己的石頭藝術品。",
      image: "https://images.unsplash.com/photo-1513519245088-0e12902e35ca?w=400&h=300&fit=crop",
      price: 450,
      duration: "2小時",
      maxParticipants: 12,
      languages: ["中文", "英文"],
      availableTimes: ["10:00", "14:00", "16:00"],
      location: "七星潭藝術村",
      category: "藝術創作",
      instructor: "黃志明 老師",
      materials: ["石頭", "顏料", "畫筆", "參考圖案"]
    }
  ];

  const openBookingModal = (activity: CultureActivity) => {
    setSelectedActivity(activity);
    setShowModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">
          {translations.culture.title}
        </h1>

        {/* Introduction */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="text-center">
            <h2 className="text-2xl font-semibold mb-4">探索花蓮在地文化</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              透過親身體驗，深入了解花蓮原住民文化的豐富內涵。從傳統料理到手工藝，
              從音樂舞蹈到藝術創作，每一項體驗都將帶給您難忘的文化之旅。
            </p>
          </div>
        </div>

        {/* Activities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cultureActivities.map((activity) => (
            <div key={activity.id} className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="relative h-48">
                <img
                  src={activity.image}
                  alt={activity.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full text-sm font-semibold text-hualien-blue">
                  NT$ {activity.price}
                </div>
                <div className="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs">
                  {activity.category}
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{activity.title}</h3>
                <p className="text-gray-600 mb-4 line-clamp-3">{activity.description}</p>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="mr-2">⏱️</span>
                    <span>{activity.duration}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="mr-2">👥</span>
                    <span>{translations.culture.maxParticipants}: {activity.maxParticipants}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="mr-2">🗣️</span>
                    <span>{translations.culture.languages}: {activity.languages.join(', ')}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="mr-2">📍</span>
                    <span>{activity.location}</span>
                  </div>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => openBookingModal(activity)}
                    className="flex-1 bg-hualien-blue text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                  >
                    {translations.culture.book}
                  </button>
                  <button className="flex-1 bg-white border border-hualien-blue text-hualien-blue py-2 px-4 rounded-lg font-medium hover:bg-hualien-light-blue transition-colors">
                    查看詳情
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Booking Modal */}
        {showModal && selectedActivity && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-semibold mb-4">{selectedActivity.title}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations.culture.timeSlots}
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    {selectedActivity.availableTimes.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations.culture.maxParticipants}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={selectedActivity.maxParticipants}
                    defaultValue="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    指導老師
                  </label>
                  <p className="text-gray-600">{selectedActivity.instructor}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    提供材料
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {selectedActivity.materials.map((material, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-hualien-light-blue text-hualien-blue text-xs rounded-full"
                      >
                        {material}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex space-x-2 mt-6">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-400 transition-colors"
                >
                  {translations.common.cancel}
                </button>
                <button
                  onClick={() => {
                    alert('預約成功！我們會盡快與您聯繫確認細節。');
                    setShowModal(false);
                  }}
                  className="flex-1 bg-hualien-blue text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  {translations.culture.book}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cultural Tips */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-semibold mb-4">🌿 文化體驗小提醒</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
            <div>
              <h3 className="font-medium mb-2">參與須知</h3>
              <ul className="space-y-1">
                <li>• 請準時到達，尊重原住民文化</li>
                <li>• 穿著舒適的服裝，部分活動可能需要戶外</li>
                <li>• 請攜帶個人用品，如飲用水</li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium mb-2">文化尊重</h3>
              <ul className="space-y-1">
                <li>• 尊重原住民傳統習俗和禁忌</li>
                <li>• 拍照前請徵求同意</li>
                <li>• 保持開放和學習的心態</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CulturePage; 